import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { DEFAULT_LANGUAGE, isLanguage, translate, type Language } from '@pmc/shared';
import { setApiLanguage } from './api';

/**
 * Language context.
 *
 * The legacy portal kept the choice in the PHP session and reloaded the page.
 * Here it lives in localStorage and re-renders in place — but it is still sent
 * to the API on every request, because some strings (FAQs, activity titles,
 * document slot labels) are resolved server-side against the database.
 */

const STORAGE_KEY = 'pmc.lang';

interface LanguageContextValue {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function readInitialLanguage(): Language {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  if (isLanguage(stored)) return stored;

  const browser = typeof navigator !== 'undefined' ? navigator.language.slice(0, 2) : '';
  return isLanguage(browser) ? browser : DEFAULT_LANGUAGE;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Language>(readInitialLanguage);

  useEffect(() => {
    setApiLanguage(lang);
    localStorage.setItem(STORAGE_KEY, lang);
    // Screen readers and browser translation both key off this.
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: Language) => setLangState(next), []);
  const t = useCallback((key: string) => translate(lang, key), [lang]);

  const value = useMemo<LanguageContextValue>(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used inside <LanguageProvider>');
  return context;
}

/** Shorthand for components that only need the translator. */
export function useT(): (key: string) => string {
  return useLanguage().t;
}
